// Booking wizard — Airfield → Date → Time → Тип модели (class) → Review.
//
// The design (ux-research.md §1b) puts aircraft last; we don't have an
// aircraft garage yet (RCF-018) so we surface class as "Тип модели" with
// human labels. When the garage lands, this step gets replaced by an
// aircraft picker that implies the class.
//
// State is a flat object held in useReducer; the wizard owns its own back
// stack via `step` so Telegram's hardware back doesn't desync the UI.

import { useEffect, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import {
  Section, Cell, List, Button, Title, Caption, Text, Spinner, Banner,
} from "@telegram-apps/telegram-ui";
import { api } from "../lib/api.js";
import { useTelegramBack } from "../lib/telegram.js";
import BookingCalendar from "../components/BookingCalendar.jsx";
import { todayInMskYmd, endOfCalendarYmd } from "../lib/calendar.js";

const STEP = {
  airfield: "airfield",
  date: "date",
  time: "time",
  classPick: "class",
  review: "review",
  done: "done",
};

const CLASS_LABELS = {
  F3A: { emoji: "✈️", title: "Самолёт", note: "Радиоуправляемые самолёты (F3A)" },
  F9U: { emoji: "🚁", title: "Дрон / коптер", note: "Мультироторы и FPV (F9U)" },
};

const RU_WEEKDAYS_SHORT = ["вс", "пн", "вт", "ср", "чт", "пт", "сб"];
const RU_MONTHS_SHORT = ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"];

function init() {
  return {
    step: STEP.airfield,
    airfields: null,
    profile: null,
    airfield: null,
    date: null,
    // Range picker state: `from` is the 30-min bucket at range start;
    // `toHhmm` is the hhmm boundary at range end (exclusive bucket-wise).
    from: null,
    toHhmm: null,
    classCode: null,
    slotsByDate: null,
    loading: false,
    error: null,
    booking: null,
  };
}

function reducer(state, action) {
  switch (action.type) {
    case "loading": return { ...state, loading: action.value, error: null };
    case "airfields": return { ...state, airfields: action.airfields, profile: action.profile ?? state.profile, loading: false };
    case "set_airfield": return { ...state, airfield: action.airfield, step: STEP.date };
    case "slots": return { ...state, slotsByDate: action.slotsByDate, loading: false };
    case "set_date": return { ...state, date: action.date, from: null, toHhmm: null, step: STEP.time };
    case "set_from": return { ...state, from: action.from, toHhmm: null };
    case "set_to": return { ...state, toHhmm: action.toHhmm, step: STEP.classPick };
    case "clear_range": return { ...state, from: null, toHhmm: null };
    case "set_class": return { ...state, classCode: action.classCode, step: STEP.review };
    case "back":
      return { ...state, error: null, step: prevStep(state) };
    case "error": return { ...state, error: action.error, loading: false };
    case "booked": return { ...state, booking: action.booking, step: STEP.done, loading: false };
    default: return state;
  }
}

function prevStep(state) {
  switch (state.step) {
    case STEP.date: return state.airfields && state.airfields.length > 1 ? STEP.airfield : STEP.date;
    case STEP.time: return STEP.date;
    case STEP.classPick: return STEP.time;
    case STEP.review: return STEP.classPick;
    default: return state.step;
  }
}

export default function Book() {
  const navigate = useNavigate();
  const [s, dispatch] = useReducer(reducer, undefined, init);

  // Telegram's hardware back walks the wizard's own back stack when there
  // is a previous step; at the wizard entry (airfield step, or date step
  // when only one airfield auto-skipped airfield) it navigates to Home so
  // pilots can bail out without force-closing the Mini App.
  const atWizardEntry =
    s.step === STEP.airfield ||
    (s.step === STEP.date && !(s.airfields && s.airfields.length > 1));
  const backHandler =
    s.step === STEP.done
      ? null
      : atWizardEntry
        ? () => navigate("/")
        : () => dispatch({ type: "back" });
  useTelegramBack(backHandler);

  // Step 0: load airfields + profile (for verification gate). Auto-skip
  // airfield step when there's only one.
  useEffect(() => {
    dispatch({ type: "loading", value: true });
    Promise.all([
      api("/api/v1/airfields"),
      api("/api/v1/me/profile").catch(() => ({ profile: null })),
    ]).then(([afRes, profRes]) => {
      dispatch({ type: "airfields", airfields: afRes.airfields, profile: profRes.profile });
      if (afRes.airfields.length === 0) {
        dispatch({ type: "error", error: "Вы пока не состоите в членах ни одного аэродрома. Попросите админа добавить вас." });
      } else if (afRes.airfields.length === 1) {
        dispatch({ type: "set_airfield", airfield: afRes.airfields[0] });
      }
    }).catch((e) => dispatch({ type: "error", error: `Не удалось загрузить аэродромы: ${e.message}` }));
  }, []);

  // Step 1→2: when airfield is chosen, fetch a 4-week slot map (today through
  // the Sunday of the 4th week). The picker renders past weekdays of the
  // current week as muted placeholders, so we only need to fetch from today.
  useEffect(() => {
    if (!s.airfield) return;
    dispatch({ type: "loading", value: true });
    const fromYmd = todayInMskYmd();
    const toYmd = endOfCalendarYmd(fromYmd);
    api(`/api/v1/airfields/${s.airfield.slug}/slots?from=${fromYmd}&to=${toYmd}&dur=30`).then((r) => {
      const grouped = groupByDate(r.days || []);
      dispatch({ type: "slots", slotsByDate: grouped });
    }).catch((e) => dispatch({ type: "error", error: `Не удалось загрузить расписание: ${e.message}` }));
  }, [s.airfield]);

  async function submit() {
    dispatch({ type: "loading", value: true });
    try {
      const startAtIso = s.from.startAtIso;
      const endAtIso = rangeEndIso(s.from, s.toHhmm);
      const r = await api("/api/v1/bookings", {
        method: "POST",
        body: JSON.stringify({
          airfield_slug: s.airfield.slug,
          class_code: s.classCode,
          start_at: startAtIso,
          end_at: endAtIso,
        }),
      });
      dispatch({ type: "booked", booking: r.booking });
    } catch (e) {
      dispatch({ type: "error", error: `Не удалось забронировать: ${e.message}. Возможно, кто-то взял этот интервал раньше — попробуйте другой.` });
    }
  }

  const stepIdx = stepIndex(s);
  return (
    <List>
      {s.step !== STEP.done && <Stepper current={stepIdx} multipleAirfields={(s.airfields?.length || 0) > 1} />}
      <Breadcrumb state={s} />
      {s.error && <Banner type="section" header="Ошибка">{s.error}</Banner>}
      {s.loading && <Section><Cell before={<Spinner size="m" />}>Загружаем…</Cell></Section>}

      {s.step === STEP.airfield && !s.loading && (
        <AirfieldStep airfields={s.airfields || []} onPick={(a) => dispatch({ type: "set_airfield", airfield: a })} />
      )}

      {s.step === STEP.date && !s.loading && s.slotsByDate && (
        <DateStep
          slotsByDate={s.slotsByDate}
          onPick={(d) => dispatch({ type: "set_date", date: d })}
          onBack={s.airfields && s.airfields.length > 1 ? () => dispatch({ type: "back" }) : null}
        />
      )}

      {s.step === STEP.time && (
        <TimeRangeStep
          slots={s.slotsByDate?.[s.date]?.slots || []}
          day={s.slotsByDate?.[s.date]}
          from={s.from}
          toHhmm={s.toHhmm}
          onPickFrom={(b) => dispatch({ type: "set_from", from: b })}
          onPickTo={(hhmm) => dispatch({ type: "set_to", toHhmm: hhmm })}
          onClear={() => dispatch({ type: "clear_range" })}
          onBack={() => dispatch({ type: "back" })}
        />
      )}

      {s.step === STEP.classPick && (
        <ClassStep
          slots={s.slotsByDate?.[s.date]?.slots || []}
          from={s.from}
          toHhmm={s.toHhmm}
          onPick={(code) => dispatch({ type: "set_class", classCode: code })}
          onBack={() => dispatch({ type: "back" })}
        />
      )}

      {s.step === STEP.review && (
        <ReviewStep
          state={s}
          verified={isVerifiedAt(s.profile, s.airfield?.slug)}
          onConfirm={submit}
          onBack={() => dispatch({ type: "back" })}
          onProfile={() => navigate("/profile")}
          loading={s.loading}
        />
      )}

      {s.step === STEP.done && s.booking && (
        <DoneStep booking={s.booking} state={s} onHome={() => navigate("/")} />
      )}
    </List>
  );
}

function groupByDate(daysArr) {
  // Server returns one entry per date with a `classes` array of
  // { class, config, buckets }. We unify the per-slot view across classes
  // so the time grid shows aggregate availability, plus carry day-level
  // metadata (weather, airspace_status, bookings_count) onto each entry.
  const out = {};
  for (const day of daysArr) {
    const slotMap = {};
    for (const cls of day.classes || []) {
      for (const b of cls.buckets || []) {
        if (!slotMap[b.hhmm]) {
          slotMap[b.hhmm] = {
            hhmm: b.hhmm, startAtIso: b.startAtIso, endAtIso: b.endAtIso,
            anyAvailable: false, totalBooked: 0, totalCapacity: 0, perClass: {},
          };
        }
        slotMap[b.hhmm].perClass[cls.class] = b;
        if (b.available) slotMap[b.hhmm].anyAvailable = true;
        slotMap[b.hhmm].totalBooked += b.booked || 0;
        if (typeof b.max_concurrent === "number") {
          slotMap[b.hhmm].totalCapacity += b.max_concurrent;
        }
      }
    }
    out[day.date] = {
      date: day.date,
      weather: day.weather,
      airspace_status: day.airspace_status || "open",
      airspace_note: day.airspace_note || null,
      bookings_count: day.bookings_count || 0,
      my_bookings_count: day.my_bookings_count || 0,
      slots: Object.values(slotMap).sort((a, b) => a.hhmm.localeCompare(b.hhmm)),
    };
  }
  return out;
}

function slotCapacityLabel(slot) {
  // "5/8 booked" if max_concurrent is finite; "Свободно" otherwise.
  if (slot.totalCapacity > 0) return `${slot.totalBooked}/${slot.totalCapacity}`;
  return slot.totalBooked > 0 ? `${slot.totalBooked}+` : "—";
}

function slotTone(slot) {
  if (!slot.anyAvailable) return "taken";
  if (slot.totalCapacity > 0) {
    const ratio = slot.totalBooked / slot.totalCapacity;
    if (ratio >= 0.75) return "filling";
  }
  return "free";
}

function slotColor(slot) {
  // Three-tier color: green (lots of room), amber (filling), red (full).
  if (!slot.anyAvailable) return "var(--tg-theme-destructive-text-color, #c80000)";
  if (slot.totalCapacity > 0) {
    const ratio = slot.totalBooked / slot.totalCapacity;
    if (ratio >= 0.75) return "#b07a00"; // amber
  }
  return "var(--tg-theme-link-color, #007aff)"; // green-equivalent
}

function stepIndex(s) {
  switch (s.step) {
    case STEP.airfield: return 0;
    case STEP.date: return 1;
    case STEP.time: return 2;
    case STEP.classPick: return 3;
    case STEP.review: return 4;
    default: return 4;
  }
}

function Stepper({ current, multipleAirfields }) {
  const labels = multipleAirfields
    ? ["Поле", "Дата", "Время", "Модель", "Готово"]
    : ["Дата", "Время", "Модель", "Готово"];
  const offset = multipleAirfields ? 0 : 1;
  return (
    <div className="rcf-stepper" aria-label="Шаги бронирования">
      {labels.map((label, i) => {
        const realIdx = i + offset;
        const cls = realIdx === current
          ? "rcf-stepper__dot rcf-stepper__dot--active"
          : realIdx < current
            ? "rcf-stepper__dot rcf-stepper__dot--done"
            : "rcf-stepper__dot";
        return (
          <span key={label} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span className={cls} aria-hidden="true" />
            <span style={{ opacity: realIdx === current ? 1 : 0.55 }}>{label}</span>
            {i < labels.length - 1 && <span style={{ opacity: 0.3 }}>›</span>}
          </span>
        );
      })}
    </div>
  );
}

function Breadcrumb({ state }) {
  const parts = [];
  if (state.airfield) parts.push(state.airfield.name);
  if (state.date) parts.push(formatDateRu(state.date));
  if (state.from && state.toHhmm) parts.push(`${state.from.hhmm}–${state.toHhmm}`);
  else if (state.from) parts.push(`с ${state.from.hhmm}`);
  if (state.classCode) parts.push(CLASS_LABELS[state.classCode]?.title || state.classCode);
  if (parts.length === 0) return null;
  return (
    <Section>
      <Cell subtitle={parts.join(" · ")} multiline>
        <Caption level="1" weight="2">Бронирование</Caption>
      </Cell>
    </Section>
  );
}

function AirfieldStep({ airfields, onPick }) {
  return (
    <Section header="Выберите аэродром">
      {airfields.map((a) => (
        <Cell key={a.slug} onClick={() => onPick(a)} subtitle={a.slug} after="→">
          🛫 {a.name}
        </Cell>
      ))}
    </Section>
  );
}

function DateStep({ slotsByDate, onPick, onBack }) {
  return (
    <>
      <Section header="Выберите дату">
        <BookingCalendar
          daysByDate={slotsByDate}
          mode="pilot"
          onPick={onPick}
        />
      </Section>
      {onBack && <BackRow onBack={onBack} />}
    </>
  );
}

// Two-stage range picker on a single screen: first tap = "Начало", second
// tap = "Конец". The "Конец" grid is gated on the "Начало" pick — only
// valid end-of-range candidates are tappable (after-from, within window,
// every bucket in between is available, hasn't been booked-through).
function TimeRangeStep({ slots, day, from, toHhmm, onPickFrom, onPickTo, onClear, onBack }) {
  if (day?.airspace_status === "closed") {
    return (
      <>
        <Banner type="section" header="Поле закрыто на этот день">
          {day.airspace_note || "Сегодня админ объявил поле закрытым. Уведомим, как только откроется."}
          <Button mode="filled" size="s" disabled>🔔 Уведомить, когда откроется</Button>
        </Banner>
        <BackRow onBack={onBack} />
      </>
    );
  }
  if (!slots || slots.length === 0) {
    return (
      <>
        <Section header="Время">
          <Cell subtitle="На эту дату нет настроенных слотов.">Слотов нет</Cell>
        </Section>
        <BackRow onBack={onBack} />
      </>
    );
  }

  const fromIdx = from ? slots.findIndex((b) => b.hhmm === from.hhmm) : -1;

  // Candidate end-of-range hhmm values. End must be > from start by at
  // least 30 minutes, must not jump over a "Занято" bucket, must not run
  // past the last bucket's tail. Each consumed bucket must be available
  // (i.e. `anyAvailable`) — capacity per class is checked at the next step.
  const endCandidates = [];
  if (fromIdx >= 0) {
    for (let j = fromIdx; j < slots.length; j++) {
      const b = slots[j];
      if (!b.anyAvailable) break;
      endCandidates.push(endOfBucketHhmm(b));
    }
  }

  return (
    <>
      {!from && (
        <Section header="Время · начало">
          <div className="rcf-timegrid">
            {slots.map((slot) => {
              const taken = !slot.anyAvailable;
              const tone = slotTone(slot);
              const cls = taken
                ? "rcf-timegrid__cell rcf-timegrid__cell--taken"
                : `rcf-timegrid__cell rcf-timegrid__cell--${tone}`;
              return (
                <button
                  key={slot.hhmm}
                  type="button"
                  className={cls}
                  disabled={taken}
                  onClick={taken ? undefined : () => onPickFrom(slot)}
                  title={taken ? "Занято" : `Свободно · ${describeSlot(slot)}`}
                >
                  <span className="rcf-timegrid__hhmm">{slot.hhmm}</span>
                  <span className="rcf-timegrid__cap">{slotCapacityLabel(slot)}</span>
                </button>
              );
            })}
          </div>
          <Cell multiline>
            <Caption level="1">
              Зелёный — свободно, жёлтый — почти полный, красный — занято. Выберите время начала.
            </Caption>
          </Cell>
        </Section>
      )}

      {from && (
        <>
          <Section header={`Конец интервала (начало ${from.hhmm})`}>
            {endCandidates.length === 0 && (
              <Cell subtitle="После выбранного начала нет свободного времени. Выберите другое начало.">
                Нет доступных вариантов
              </Cell>
            )}
            {endCandidates.map((hhmm) => {
              const mins = minutesBetween(from.hhmm, hhmm);
              return (
                <Cell
                  key={hhmm}
                  onClick={() => onPickTo(hhmm)}
                  subtitle={`Длительность: ${formatDuration(mins)}`}
                  after="→"
                >
                  до {hhmm}
                </Cell>
              );
            })}
          </Section>
          <Section>
            <Cell onClick={onClear} after="↩">← Выбрать другое начало</Cell>
          </Section>
        </>
      )}
      <BackRow onBack={onBack} />
    </>
  );
}

function ClassStep({ slots, from, toHhmm, onPick, onBack }) {
  // A class is bookable for the range iff every 30-min bucket in
  // [from, toHhmm) has that class as `available`.
  const fromIdx = slots.findIndex((b) => b.hhmm === from?.hhmm);
  const consumed = fromIdx >= 0
    ? slots.slice(fromIdx, fromIdx + bucketCount(from.hhmm, toHhmm))
    : [];
  const options = Object.entries(CLASS_LABELS)
    .filter(([code]) =>
      consumed.length > 0 && consumed.every((b) => b.perClass?.[code]?.available)
    );
  return (
    <>
      <Section header="Тип модели">
        {options.length === 0 && (
          <Cell subtitle="В выбранном интервале нет свободных классов. Вернитесь и попробуйте другой интервал." multiline>
            Нет вариантов
          </Cell>
        )}
        {options.map(([code, meta]) => (
          <Cell key={code} onClick={() => onPick(code)} subtitle={meta.note} after="→" multiline>
            {meta.emoji} {meta.title}
          </Cell>
        ))}
      </Section>
      <BackRow onBack={onBack} />
    </>
  );
}

function ReviewStep({ state, verified, onConfirm, onBack, onProfile, loading }) {
  const cls = CLASS_LABELS[state.classCode];
  const durMin = minutesBetween(state.from.hhmm, state.toHhmm);
  return (
    <>
      <Section header="Проверьте и подтвердите">
        <Cell subtitle="Аэродром">{state.airfield.name}</Cell>
        <Cell subtitle="Дата">{formatDateRu(state.date)}</Cell>
        <Cell subtitle="Время" multiline>
          {state.from.hhmm}–{state.toHhmm} ({formatDuration(durMin)})
        </Cell>
        <Cell subtitle="Тип модели">{cls?.emoji} {cls?.title}</Cell>
      </Section>
      {!verified && (
        <Banner type="section" header="Профиль не подтверждён">
          Бронирование заблокировано до подтверждения профиля админом. Откройте профиль, чтобы заполнить документы и попросить проверку.
          <Button mode="filled" size="s" onClick={onProfile}>Открыть профиль</Button>
        </Banner>
      )}
      <Section>
        <Cell>
          <Button
            stretched
            mode="filled"
            disabled={loading || !verified}
            onClick={onConfirm}
          >
            {loading ? "Бронируем…" : verified ? "Подтвердить бронирование" : "🔒 Подтвердите профиль"}
          </Button>
        </Cell>
        <Cell>
          <Button stretched mode="plain" disabled={loading} onClick={onBack}>
            ← Назад
          </Button>
        </Cell>
      </Section>
    </>
  );
}

function isVerifiedAt(profile, airfieldSlug) {
  if (!profile || !airfieldSlug) return false;
  const m = (profile.memberships || []).find((x) => x.airfield_slug === airfieldSlug);
  return m?.verification_status === "verified";
}

function DoneStep({ booking, state, onHome }) {
  return (
    <Section header="Готово">
      <Cell subtitle={`#${booking.id}`}>
        <Title level="3">Слот забронирован ✅</Title>
      </Cell>
      <Cell subtitle={`${formatDateRu(state.date)}, ${state.from.hhmm}–${state.toHhmm}`}>
        {state.airfield.name}
      </Cell>
      <Cell>
        <Text>
          За час до начала уже не сможете отменить через Mini App — попросите админа.
        </Text>
      </Cell>
      <Cell>
        <Button stretched onClick={onHome}>На главную</Button>
      </Cell>
    </Section>
  );
}

function BackRow({ onBack, inline = false }) {
  return (
    <Section {...(inline ? {} : { header: " " })}>
      <Cell onClick={onBack}>← Назад</Cell>
    </Section>
  );
}

function describeSlot(slot) {
  const parts = [];
  for (const [code, b] of Object.entries(slot.perClass || {})) {
    parts.push(`${code} ${b.available ? "✓" : "✗"}`);
  }
  return parts.join(" · ") || "—";
}

// Range helpers for the from-to picker.
function endOfBucketHhmm(bucket) {
  // 30-min buckets — end = start + 30 minutes.
  const [h, m] = bucket.hhmm.split(":").map(Number);
  const total = h * 60 + m + 30;
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

function minutesBetween(fromHhmm, toHhmm) {
  const [fh, fm] = fromHhmm.split(":").map(Number);
  const [th, tm] = toHhmm.split(":").map(Number);
  return (th * 60 + tm) - (fh * 60 + fm);
}

function bucketCount(fromHhmm, toHhmm) {
  return Math.max(1, Math.round(minutesBetween(fromHhmm, toHhmm) / 30));
}

function formatDuration(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (h === 0) return `${m} мин`;
  if (m === 0) return `${h} ч`;
  return `${h} ч ${m} мин`;
}

function rangeEndIso(fromBucket, toHhmm) {
  // fromBucket.startAtIso is the absolute timestamp of the range start;
  // add (toHhmm - fromBucket.hhmm) minutes to get the range end.
  const minutes = minutesBetween(fromBucket.hhmm, toHhmm);
  const startMs = Date.parse(fromBucket.startAtIso);
  return new Date(startMs + minutes * 60000).toISOString();
}

function endHhmm(slot) {
  // The previous "regex out HH:MM from endAtIso" parsed the UTC component
  // of the ISO string instead of MSK — leading to a "08:30–05:30" style
  // breadcrumb when local time differed from UTC. Compute end-of-slot
  // from the duration (UTC-agnostic) and add it to the start hhmm.
  if (!slot?.startAtIso || !slot?.endAtIso || !slot?.hhmm) return "—";
  const startMs = Date.parse(slot.startAtIso);
  const endMs = Date.parse(slot.endAtIso);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return "—";
  const durationMin = Math.round((endMs - startMs) / 60000);
  const [h, m] = slot.hhmm.split(":").map(Number);
  const total = h * 60 + m + durationMin;
  const hh = String(Math.floor(total / 60) % 24).padStart(2, "0");
  const mm = String(total % 60).padStart(2, "0");
  return `${hh}:${mm}`;
}

function formatDateRu(ymd) {
  // ymd is "YYYY-MM-DD". Derive the weekday from a UTC date so we don't drift
  // by tz when the user's device is in a non-MSK zone.
  const [y, m, d] = ymd.split("-").map(Number);
  const utc = new Date(Date.UTC(y, m - 1, d));
  return `${RU_WEEKDAYS_SHORT[utc.getUTCDay()]} ${d} ${RU_MONTHS_SHORT[m - 1]}`;
}
